/**
 * lod0_ledger — the per-swap ledger of the settlement LOD refinement
 * (settlement LOD0 research L9, 2026-09-03). Pure; the settlement module
 * feeds it, the perf HUD and the walk profile print it.
 *
 * The refinement used to COUNT swaps and loads; it never timed them. Every
 * entry records where the level's bytes came from (pack, the in-memory
 * bytes cache, or the network), how long the fetch and the parse took, how
 * many bytes and residency tasks the swap uploaded, how long the building
 * waited as a candidate, and the time from the swap to its crossfade
 * starting — the number a player perceives. The last LOD0_LEDGER_KEEP entries
 * stay; the summary is medians (robust to a SwiftShader outlier).
 */

export type LoadSource = 'pack' | 'memory' | 'network' | 'cache';

export interface LoadMetrics { source: LoadSource; wireBytes: number; fetchMs: number; parseMs: number }

export interface Lod0LedgerEntry {
  key: string; site: number; level: number;
  source: LoadSource; wireBytes: number; fetchMs: number; parseMs: number;
  /** ms the building waited between first becoming a candidate and its load starting. */
  queueMs: number;
  /** ms from the load starting to the master being usable. */
  loadMs: number;
  /** bytes and residency tasks the swap's batches uploaded (S1 planner). */
  uploadBytes: number; uploadTasks: number;
  /** ms from the swap (instances born at fade 0) to the crossfade starting (batches resident). */
  swapToFadeMs: number;
  at: number;
}

export const LOD0_LEDGER_KEEP = 20;

function median(xs: number[]): number {
  if (xs.length === 0) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

export interface Lod0LedgerSummary {
  n: number; lod0: number;
  medFetchMs: number; medParseMs: number; medQueueMs: number; medLoadMs: number; medSwapToFadeMs: number;
  wireMb: number; uploadMb: number; uploadMbPerSwap: number; tasksPerSwap: number;
  bySource: Record<LoadSource, number>;
}

export class Lod0Ledger {
  readonly entries: Lod0LedgerEntry[] = [];
  /** Lifetime tallies (the ring only keeps the last few entries). */
  total = 0; totalWire = 0; totalUpload = 0;
  constructor(private readonly keep = LOD0_LEDGER_KEEP) {}

  add(e: Lod0LedgerEntry): void {
    this.entries.push(e);
    if (this.entries.length > this.keep) this.entries.shift();
    this.total++; this.totalWire += e.wireBytes; this.totalUpload += e.uploadBytes;
  }

  summary(): Lod0LedgerSummary {
    const es = this.entries;
    const bySource: Record<LoadSource, number> = { pack: 0, memory: 0, network: 0, cache: 0 };
    for (const e of es) bySource[e.source]++;
    const upload = es.reduce((s, e) => s + e.uploadBytes, 0);
    return {
      n: es.length, lod0: es.filter((e) => e.level === 0).length,
      medFetchMs: +median(es.map((e) => e.fetchMs)).toFixed(1), medParseMs: +median(es.map((e) => e.parseMs)).toFixed(1),
      medQueueMs: +median(es.map((e) => e.queueMs)).toFixed(0), medLoadMs: +median(es.map((e) => e.loadMs)).toFixed(0),
      medSwapToFadeMs: +median(es.map((e) => e.swapToFadeMs)).toFixed(0),
      wireMb: +(es.reduce((s, e) => s + e.wireBytes, 0) / 1048576).toFixed(2), uploadMb: +(upload / 1048576).toFixed(2),
      uploadMbPerSwap: es.length ? +(upload / es.length / 1048576).toFixed(2) : 0,
      tasksPerSwap: es.length ? +(es.reduce((s, e) => s + e.uploadTasks, 0) / es.length).toFixed(1) : 0,
      bySource,
    };
  }

  /** One HUD line. */
  line(): string {
    const s = this.summary();
    if (s.n === 0) return 'lod0 ledger: no swaps yet';
    const src = Object.entries(s.bySource).filter(([, n]) => n > 0).map(([k, n]) => `${k} ${n}`).join(' / ');
    return `lod swaps ${this.total} (last ${s.n}, ${s.lod0} to LOD0) · fetch ${s.medFetchMs} ms · parse ${s.medParseMs} ms · queue ${s.medQueueMs} ms · upload ${s.uploadMbPerSwap} MB in ${s.tasksPerSwap} tasks/swap · swap→fade ${s.medSwapToFadeMs} ms · ${src}`;
  }
}

/** The one ledger the settlement module writes and the HUD/profile read. */
export const lod0Ledger = new Lod0Ledger();
